/**
 * TransferirClienteDialog — Transfere um veículo (e todas as OS/inspeções/orçamentos)
 * para outro cliente com auditoria e proteção do Portal do Cliente.
 *
 * Fluxos:
 *   1. Selecionar cliente existente (busca por nome/telefone/e-mail)
 *   2. Criar novo cliente inline
 *   3. Confirmação obrigatória antes de persistir
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { User as UserIcon, UserPlus, ArrowRight, AlertTriangle, Search, Check, ArrowLeft } from "lucide-react";

const TIPOS = ["Particular", "Garantia", "Frota", "Body Shop"];

export default function TransferirClienteDialog({ vehicle, currentClient, open, onOpenChange, onTransferred }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [step, setStep] = useState("select"); // select | confirm
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: "", phone: "", email: "", address: "", tipo: "Particular", observacoes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("existing");
      setStep("select");
      setSearch("");
      setSelectedClient(null);
      setNewClient({ name: "", phone: "", email: "", address: "", tipo: "Particular", observacoes: "" });
      (async () => {
        try {
          const { data } = await api.get("/clients");
          setClients(data || []);
        } catch { /* silencioso */ }
      })();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = clients.filter((c) => c.id !== currentClient?.id);
    if (!q) return list.slice(0, 12);
    return list.filter((c) => {
      const hay = `${c.name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 50);
  }, [clients, search, currentClient]);

  const goToConfirm = () => {
    if (mode === "existing") {
      if (!selectedClient) return toast.error("Selecione um cliente da lista");
    } else {
      if (!newClient.name.trim()) return toast.error("Informe o nome do novo cliente");
    }
    setStep("confirm");
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = mode === "existing"
        ? { client_id: selectedClient.id }
        : { new_client: {
            name: newClient.name.trim(),
            phone: newClient.phone.trim() || null,
            email: newClient.email.trim() || null,
            address: newClient.address.trim() || null,
            tipo: newClient.tipo,
            observacoes: newClient.observacoes.trim() || null,
          } };
      const { data } = await api.post(`/vehicles/${vehicle.id}/transfer`, payload);
      toast.success(`Veículo transferido para ${data.to_client_name}. ${data.affected_jobs} OS reassociada(s).`);
      onOpenChange(false);
      onTransferred && onTransferred(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao transferir");
    } finally {
      setSaving(false);
    }
  };

  const targetName = mode === "existing" ? selectedClient?.name : newClient.name.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="transfer-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="w-4 h-4" /> Transferir Cliente
          </DialogTitle>
          <DialogDescription>
            Altere o cliente proprietário deste veículo. Todas as OS, inspeções e orçamentos serão movidos junto — o cliente anterior perde acesso automaticamente no Portal.
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
              <div className="font-medium text-foreground mb-1">Veículo:</div>
              <div>{vehicle?.year} {vehicle?.make} {vehicle?.model} · {vehicle?.plate || vehicle?.vin || "—"}</div>
              <div className="mt-2 font-medium text-foreground">Cliente atual:</div>
              <div>{currentClient?.name || "—"} {currentClient?.phone ? `· ${currentClient.phone}` : ""}</div>
            </div>

            <Tabs value={mode} onValueChange={setMode} className="w-full">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="existing" data-testid="tab-existing-client">
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Cliente Existente
                </TabsTrigger>
                <TabsTrigger value="new" data-testid="tab-new-client">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Cadastrar Novo
                </TabsTrigger>
              </TabsList>

              <TabsContent value="existing" className="mt-4 space-y-3">
                <div>
                  <Label>Buscar cliente</Label>
                  <Input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Nome, telefone ou e-mail…"
                    data-testid="transfer-search-input"
                  />
                </div>
                <div className="border border-border rounded-md max-h-72 overflow-y-auto divide-y divide-border">
                  {filtered.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">
                      {search ? "Nenhum cliente encontrado" : "Digite para buscar"}
                    </div>
                  ) : filtered.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedClient(c)}
                      className={`w-full text-left p-3 flex items-start gap-3 hover:bg-secondary/60 transition ${selectedClient?.id === c.id ? "bg-secondary" : ""}`}
                      data-testid={`transfer-option-${c.id}`}
                    >
                      <div className={`w-8 h-8 shrink-0 rounded-full border ${selectedClient?.id === c.id ? "border-foreground bg-foreground text-background" : "border-border bg-secondary text-muted-foreground"} flex items-center justify-center`}>
                        {selectedClient?.id === c.id ? <Check className="w-4 h-4" /> : <UserIcon className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[c.phone, c.email].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border text-muted-foreground">
                        {c.tipo || "Particular"}
                      </span>
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="new" className="mt-4 space-y-3">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    autoFocus
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    data-testid="transfer-new-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Telefone</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      data-testid="transfer-new-phone"
                    />
                  </div>
                  <div>
                    <Label>E-mail</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      data-testid="transfer-new-email"
                    />
                  </div>
                </div>
                <div>
                  <Label>Endereço</Label>
                  <Input
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    data-testid="transfer-new-address"
                  />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={newClient.tipo} onValueChange={(v) => setNewClient({ ...newClient, tipo: v })}>
                    <SelectTrigger data-testid="transfer-new-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Observações</Label>
                  <Textarea rows={2} value={newClient.observacoes} onChange={(e) => setNewClient({ ...newClient, observacoes: e.target.value })} />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="button" onClick={goToConfirm} data-testid="transfer-next-btn">
                Continuar <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-border rounded-md p-4 bg-secondary/30">
              <div className="flex items-center gap-2 text-foreground font-semibold mb-3">
                <AlertTriangle className="w-4 h-4" /> Confirmação da transferência
              </div>
              <div className="text-sm space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Veículo</div>
                  <div className="font-medium">{vehicle?.year} {vehicle?.make} {vehicle?.model} · {vehicle?.plate || vehicle?.vin || "—"}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">De</div>
                    <div className="line-through text-muted-foreground">{currentClient?.name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Para</div>
                    <div className="font-semibold">
                      {targetName}
                      {mode === "new" && <span className="ml-1.5 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border">NOVO</span>}
                    </div>
                  </div>
                </div>
              </div>
              <ul className="text-xs text-muted-foreground mt-4 space-y-1 list-disc pl-5">
                <li>Todas as OS, inspeções, orçamentos e fotos deste veículo serão reassociadas.</li>
                <li>O cliente <b>{currentClient?.name}</b> deixará de ver este veículo no Portal imediatamente.</li>
                <li>O cliente <b>{targetName}</b> passará a ter acesso a todo o histórico no Portal.</li>
                <li>A transferência será registrada na auditoria (data, hora e usuário responsável).</li>
              </ul>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep("select")} disabled={saving}>
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
              </Button>
              <Button type="button" onClick={submit} disabled={saving} data-testid="transfer-confirm-btn">
                {saving ? "Transferindo…" : "Confirmar Transferência"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
